import { createSlice } from '@reduxjs/toolkit';
import { isArray, isObject, xor } from 'lib';
import { getTimestamp, magicNumbers } from 'utils';
import {
  handleResetFilter,
  handleSetSelectedCategories,
  handleSetSelectedContentBasedValues,
  handleSetSelectedFilterIds,
  handleSetSelectedFilterValue,
  handleSetSelectedItems,
  handleSetSelectedSentimentValues,
  handleToggleCollapsed,
} from 'store/helpers/filterHelpers';
import { dashboardInitialState } from './dashboard.initialState';
import {
  deleteStream,
  fetchDashboards,
  fetchStreamContents,
  saveStream,
} from './dashboard.actions';
import { streamAdaptor } from './dashboard.adapter';

export const dashboardSlice = createSlice({
  name: 'dashboardSlice',
  initialState: dashboardInitialState,
  reducers: {
    setSelectedFilterIds: (state, action) => {
      handleSetSelectedFilterIds(state, action);
    },
    setSelectedItems: (state, action) => {
      handleSetSelectedItems(state, action);
    },
    setSelectedFilterValue: (state, action) => {
      handleSetSelectedFilterValue(
        state,
        action,
        dashboardInitialState.filters
      );
    },
    setSelectedContentBasedValues: (state, action) => {
      handleSetSelectedContentBasedValues(state, action);
    },
    setSelectedCategories: (state, action) => {
      handleSetSelectedCategories(state, action);
    },
    toggleCollapsed: (state, action) => {
      handleToggleCollapsed(state, action);
    },
    resetFilter: (state, action) => {
      handleResetFilter(state, action, dashboardInitialState.filters);
    },
    setSelectedFilter: (state, action) => {
      state.selectedFilter = action.payload;
    },
    setStreamParamValue: (state, action) => {
      const { key, value, params = false } = action.payload;
      if (params) {
        state.stream.params[key] = value;
      } else {
        state.stream[key] = value;
      }
    },
    setSelectedSentimentValues: (state, action) => {
      handleSetSelectedSentimentValues(state, action);
    },
    changeStreamPlatform: (state, action) => {
      const platform = action.payload;
      state.stream.params.platforms = xor(state.stream.params.platforms, [
        platform,
      ]);
    },
    resetCurrentStream: (state) => {
      state.stream = dashboardInitialState.stream;
      state.filters = dashboardInitialState.filters;
    },
    toggleStreamDrawer: (state) => {
      state.isStreamDrawerOpened = !state.isStreamDrawerOpened;
    },
    setStreamValues: (state, action) => {
      const { stream, filters } = streamAdaptor(action.payload);

      state.stream = stream;
      state.filters = filters;
      state.isStreamDrawerOpened = true;
    },
    countPollLastContent: (state, action) => {
      const { streamId } = action.payload;
      if (!isArray(state.contents[streamId]?.data)) {
        return;
      }
      if (!isArray(state.scrolledContents[streamId])) {
        state.scrolledContents[streamId] = [];
      }
      const lastNewContent = state.newContents[streamId].data.shift();
      if (lastNewContent) {
        state.scrolledContents[streamId].unshift(lastNewContent);
        if (state.scrolledContents[streamId].length > 51) {
          state.scrolledContents[streamId].pop();
        }
      }
      state.newContents[streamId].isBatchUpdate = false;
    },
    displayAllCountedContents: (state, action) => {
      const { streamId } = action.payload;
      state.contents[streamId].data.unshift(
        ...state.scrolledContents[streamId]
      );
      state.scrolledContents[streamId] = [];
      if (state.contents[streamId].data.length > 50) {
        state.contents[streamId].data.slice(0, 50);
      }
    },
    displayPollLastContent: (state, action) => {
      const { streamId } = action.payload;
      if (
        !isArray(state.contents[streamId].data) ||
        !isArray(state.newContents[streamId].data)
      ) {
        return;
      }
      const lastNewContent = state.newContents[streamId].data.shift();
      if (lastNewContent) {
        lastNewContent.isNew = true;
        state.contents[streamId].data.unshift(lastNewContent);
        if (state.contents[streamId].data.length > 50) {
          state.contents[streamId].data.pop();
        }
      }
      state.newContents[streamId].isBatchUpdate = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDashboards.fulfilled, (state, action) => {
        const dashboards = action.payload;
        state.dashboards.data = dashboards;
        state.dashboards.loading = false;
        state.dashboards.error = null;

        if (dashboards.length > 0) {
          state.dashboardId = dashboards?.[0]?.id;
        }
      })
      .addCase(fetchDashboards.pending, (state) => {
        state.dashboards.loading = true;
        state.dashboards.error = null;
      })
      .addCase(fetchDashboards.rejected, (state, action) => {
        state.dashboards.loading = false;
        state.dashboards.error = action.payload;
      })
      .addCase(fetchStreamContents.pending, (state, action) => {
        const { streamId, isPolling } = action.meta.arg;
        if (isPolling) {
          return;
        }
        if (!state.contents[streamId]) {
          state.contents[streamId] = {};
        }
        state.contents[streamId].error = null;
        state.contents[streamId].loading = true;
      })
      .addCase(fetchStreamContents.rejected, (state, action) => {
        const { streamId, isPolling } = action.meta.arg;
        if (isPolling) {
          return;
        }
        if (!state.contents[streamId]) {
          state.contents[streamId] = {};
        }
        state.contents[streamId].loading = false;
        state.contents[streamId].error = action.payload;
      })
      .addCase(fetchStreamContents.fulfilled, (state, action) => {
        const { streamId, isPolling } = action.meta.arg;
        const contents = action.payload;

        if (contents?.[0]?.insertedAt) {
          state.dashboards.data[0].streams.forEach((stream, index) => {
            if (stream.id === streamId) {
              state.dashboards.data[0].streams[
                index
              ].params.document_inserted_at = getTimestamp(
                contents[0].insertedAt
              );
            }
          });
        }

        if (isPolling) {
          if (!isObject(state.newContents[streamId])) {
            state.newContents[streamId] = {};
            state.newContents[streamId].data = [];
          }
          state.newContents[streamId].isBatchUpdate = true;
          contents.reverse().forEach((content) => {
            const isInPoll =
              state.newContents[streamId].data.findIndex(
                (newContent) => newContent.id === content.id
              ) > magicNumbers.INDEX_NOT_FOUND;
            const isDisplaying =
              state.contents[streamId].data.findIndex(
                (displayedContent) => displayedContent.id === content.id
              ) > magicNumbers.INDEX_NOT_FOUND;
            if (!isInPoll && !isDisplaying)
              state.newContents[streamId].data.push(content);
          });
          state.newContents[streamId].data = state.newContents[
            streamId
          ].data.slice(0, magicNumbers.LIMIT_OF_VISIBLE_STREAM_CONTENTS);
          return;
        }

        if (!isArray(state.contents[streamId].data)) {
          state.contents[streamId].data = [];
        }

        state.contents[streamId].data = contents;
        state.contents[streamId].loading = false;
        state.contents[streamId].error = null;
      })
      .addCase(saveStream.fulfilled, (state, action) => {
        const { streamId } = state.stream;
        if (streamId) {
          if (isArray(state.newContents?.[streamId]?.data)) {
            state.newContents[streamId].data = [];
          }
          state.dashboards.data[0].streams.forEach((stream, index) => {
            if (stream.id === streamId) {
              state.dashboards.data[0].streams[index] = {
                ...action.payload,
                id: streamId,
                dashboard_id: stream.dashboard_id,
                updateId: stream.updateId ? stream.updateId + 1 : 1,
              };
            }
          });
        } else if (action.payload)
          state.dashboards.data[0].streams.push(action.payload);

        state.stream = dashboardInitialState.stream;
        state.saveStreamRequest.loading = false;
        state.saveStreamRequest.error = null;
      })
      .addCase(saveStream.pending, (state) => {
        state.saveStreamRequest.loading = true;
        state.saveStreamRequest.error = null;
      })
      .addCase(saveStream.rejected, (state, action) => {
        state.saveStreamRequest.loading = false;
        state.saveStreamRequest.error = action.payload;
      })
      .addCase(deleteStream.pending, (state) => {
        state.deleteStream.loading = true;
        state.deleteStream.error = null;
      })
      .addCase(deleteStream.rejected, (state, action) => {
        state.deleteStream.loading = false;
        state.deleteStream.error = action.payload;
      })
      .addCase(deleteStream.fulfilled, (state, action) => {
        const { streamId } = action.meta.arg;
        state.dashboards.data[0].streams =
          state.dashboards.data[0].streams.filter(
            (stream) => stream.id !== streamId
          );
        state.deleteStream.loading = false;
        state.deleteStream.error = null;
      });
  },
});

export const {
  setSelectedFilterIds,
  setSelectedItems,
  setSelectedFilterValue,
  setSelectedContentBasedValues,
  setSelectedCategories,
  toggleCollapsed,
  resetFilter,
  setSelectedFilter,
  setStreamParamValue,
  changeStreamPlatform,
  resetCurrentStream,
  toggleStreamDrawer,
  setStreamValues,
  setSelectedSentimentValues,
  displayPollLastContent,
  countPollLastContent,
  displayAllCountedContents,
} = dashboardSlice.actions;
